// AI-ASSISTED: generated with claude-code on 2026-06-29, reviewed by Yoann Hertienne
import { ReactNode } from 'react';
import useAsync from 'react-use/lib/useAsync';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardActions from '@mui/material/CardActions';
import CardContent from '@mui/material/CardContent';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import Link from '@mui/material/Link';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import Typography from '@mui/material/Typography';
import { discoveryApiRef, fetchApiRef, useApi } from '@backstage/core-plugin-api';
import { useEntity } from '@backstage/plugin-catalog-react';
import { TileBoundary } from '../team-landing/TileBoundary';

// Drives the whole card. CF Components are hosted on Bitbucket Cloud, so the
// slug is `<workspace>/<repo>` (e.g. `edfluminus/backstage`). When absent the
// card hides, matching the other US4 tiles (spec FR-008 hide-on-absence).
const REPO_SLUG_ANNOTATION = 'bitbucket.org/repo-slug';

/** A normalised, render-ready row shared by all three subsections. */
export type SectionItem = {
  /** Stable React key, unique within the section. */
  key: string;
  primary: string;
  secondary?: string;
  /** Bitbucket Cloud web URL for the row, opened in a new tab. */
  href?: string;
};

type RawConnection = { values?: unknown[] };

/** Returns `payload.values` as an array, or `[]` for any other shape. */
const valuesOf = (payload: unknown): unknown[] => {
  const values = (payload as RawConnection | null | undefined)?.values;
  return Array.isArray(values) ? values : [];
};

/** Trims an ISO timestamp to its `YYYY-MM-DD` date, or `undefined`. */
const toDate = (value: unknown): string | undefined =>
  typeof value === 'string' && value.length >= 10 ? value.slice(0, 10) : undefined;

type RawPullRequest = {
  id?: number;
  title?: string;
  author?: { display_name?: string };
  updated_on?: string;
  links?: { html?: { href?: string } };
};

/** Maps a Bitbucket Cloud v2 `pullrequests` payload to render rows. */
export const parsePullRequests = (payload: unknown): SectionItem[] =>
  valuesOf(payload).map((value, index) => {
    const pr = value as RawPullRequest;
    const id = typeof pr.id === 'number' ? pr.id : index;
    const updated = toDate(pr.updated_on);
    const author = pr.author?.display_name ?? 'unknown';
    return {
      key: `pr-${id}`,
      primary: `#${id} ${pr.title ?? 'untitled'}`,
      secondary: updated ? `${author} · updated ${updated}` : author,
      href: pr.links?.html?.href,
    };
  });

type RawTag = {
  name?: string;
  target?: { date?: string };
  links?: { html?: { href?: string } };
};

/** Maps a Bitbucket Cloud v2 `refs/tags` payload to render rows. */
export const parseTags = (payload: unknown): SectionItem[] =>
  valuesOf(payload).map((value, index) => {
    const tag = value as RawTag;
    const name = tag.name ?? `tag-${index}`;
    const date = toDate(tag.target?.date);
    return {
      key: `tag-${name}`,
      primary: name,
      secondary: date ? `tagged ${date}` : undefined,
      href: tag.links?.html?.href,
    };
  });

type RawCommit = {
  hash?: string;
  message?: string;
  date?: string;
  author?: { user?: { display_name?: string }; raw?: string };
  links?: { html?: { href?: string } };
};

/** Maps a Bitbucket Cloud v2 `commits` payload to render rows. */
export const parseCommits = (payload: unknown): SectionItem[] =>
  valuesOf(payload).map((value, index) => {
    const commit = value as RawCommit;
    const hash = commit.hash ?? `commit-${index}`;
    const subject = (commit.message ?? '').split('\n')[0].trim() || '(no message)';
    const author = commit.author?.user?.display_name ?? commit.author?.raw ?? 'unknown';
    const date = toDate(commit.date);
    const meta = [author, date, hash.slice(0, 7)].filter(Boolean).join(' · ');
    return {
      key: `commit-${hash}`,
      primary: subject,
      secondary: meta,
      href: commit.links?.html?.href,
    };
  });

/** Encodes `<workspace>/<repo>` for a URL path while preserving the slash. */
export const encodeSlug = (slug: string): string =>
  slug
    .split('/')
    .map(part => encodeURIComponent(part))
    .join('/');

type SectionProps = {
  title: string;
  slug: string;
  /** Path + query under `/repositories/{slug}/`, e.g. `pullrequests?state=OPEN`. */
  path: string;
  parse: (payload: unknown) => SectionItem[];
  emptyLabel: string;
};

/**
 * One subsection (PRs / tags / commits). Fetches independently and re-throws on
 * error so the enclosing per-section TileBoundary degrades this section only,
 * leaving the other two intact (DPT-499 boundary-granularity decision, FR-008).
 */
const Section = ({ title, slug, path, parse, emptyLabel }: SectionProps) => {
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);

  const { value, loading, error } = useAsync(async () => {
    const baseUrl = await discoveryApi.getBaseUrl('proxy');
    const url = `${baseUrl}/bitbucket/2.0/repositories/${encodeSlug(slug)}/${path}`;
    const response = await fetchApi.fetch(url);
    if (!response.ok) {
      throw new Error(`Bitbucket ${title} request failed: ${response.status}`);
    }
    return parse(await response.json());
  }, [slug, path]);

  if (error) throw error;

  const items = value ?? [];

  return (
    <Box sx={{ pt: 1 }}>
      <Typography variant="subtitle2">{title}</Typography>
      {loading && <CircularProgress size={18} aria-label={`Loading ${title}`} />}
      {!loading && items.length === 0 && (
        <Typography variant="body2" color="text.secondary">
          {emptyLabel}
        </Typography>
      )}
      {!loading && items.length > 0 && (
        <List dense aria-label={title} sx={{ py: 0 }}>
          {items.map(item => (
            <ListItem key={item.key} disableGutters sx={{ py: 0.25 }}>
              <ListItemText
                primary={
                  item.href ? (
                    <Link href={item.href} target="_blank" rel="noreferrer">
                      {item.primary}
                    </Link>
                  ) : (
                    item.primary
                  )
                }
                secondary={item.secondary}
              />
            </ListItem>
          ))}
        </List>
      )}
    </Box>
  );
};

const BitbucketCardBody = ({ slug }: { slug: string }) => {
  const repoUrl = `https://bitbucket.org/${slug}`;
  return (
    <Card variant="outlined">
      <CardContent>
        <Typography variant="h6">Bitbucket</Typography>
        <Typography variant="body2" color="text.secondary">
          {slug}
        </Typography>

        {/* Each subsection owns its TileBoundary so one failing endpoint
            degrades only its block, not the whole card (DPT-499 / FR-008). */}
        <TileBoundary tool="Bitbucket pull requests">
          <Section
            title="Open pull requests"
            slug={slug}
            path="pullrequests?state=OPEN&pagelen=10&sort=-updated_on"
            parse={parsePullRequests}
            emptyLabel="No open pull requests."
          />
        </TileBoundary>
        <Divider sx={{ mt: 1 }} />
        <TileBoundary tool="Bitbucket tags">
          <Section
            title="Recent tags"
            slug={slug}
            path="refs/tags?pagelen=10&sort=-target.date"
            parse={parseTags}
            emptyLabel="No tags."
          />
        </TileBoundary>
        <Divider sx={{ mt: 1 }} />
        <TileBoundary tool="Bitbucket commits">
          <Section
            title="Recent commits"
            slug={slug}
            path="commits?pagelen=5"
            parse={parseCommits}
            emptyLabel="No commits."
          />
        </TileBoundary>
      </CardContent>
      <CardActions>
        <Button
          variant="outlined"
          size="small"
          href={repoUrl}
          target="_blank"
          rel="noreferrer"
          aria-label="Open this repository in Bitbucket"
        >
          Open in Bitbucket
        </Button>
      </CardActions>
    </Card>
  );
};

/**
 * Bitbucket Cloud entity card — open PRs, recent tags and recent commits for
 * the repository named by the `bitbucket.org/repo-slug` annotation. Replaces
 * the Azure DevOps Git Tags + Pull Requests sub-tabs disabled in DPT-488
 * (CF Components are hosted on Bitbucket Cloud, not Azure Repos). Renders
 * `null` when the annotation is absent. Reads via the read-only `/bitbucket`
 * proxy route; never writes.
 */
export const BitbucketCard = (): ReactNode => {
  const { entity } = useEntity();
  const slug = entity.metadata.annotations?.[REPO_SLUG_ANNOTATION];
  if (!slug) return null;
  return <BitbucketCardBody slug={slug} />;
};
